/** Nombres de los meses en español, indexados 0–11 (como `Date.getMonth`). */
export const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/** Nombres de los días de la semana en español, indexados 0–6 (como
 *  `Date.getDay`: 0 = domingo). */
const DIAS_SEMANA = [
  "Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado",
];

/** Abreviaturas de meses en español (3 letras). */
export const MESES_ABR = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

/** Abreviaturas de días de la semana en español (3 letras), indexadas 0–6
 *  (como `Date.getDay`: 0 = domingo). */
const DIAS_SEMANA_ABR = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

/** Normaliza un ISO (con o sin hora) a "yyyy-mm-dd". */
export function toDateOnly(isoDate: string): string {
  return isoDate.slice(0, 10);
}

/** ISO (con o sin hora) → `Date` local a medianoche. Tolera "2026-05-31" y
 *  "2026-05-31T10:00:00Z" por igual. */
function _toLocalDate(isoDate: string): Date {
  return new Date(toDateOnly(isoDate) + "T00:00:00");
}

/** Retorna "Mes Día" a partir de una fecha ISO (ej: "Marzo 15"). */
export function formatMesDia(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return `${MESES[d.getMonth()]} ${d.getDate()}`;
}

/** Retorna "Mes DD" a partir de una fecha ISO sin hora, con el día siempre a
 *  2 dígitos (ej: "Julio 07"). A diferencia de `formatMesDia` (día sin
 *  padear), para alinear visualmente con `formatMesDiaHora`. */
export function formatMesDiaPadded(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  const day = String(d.getDate()).padStart(2, "0");
  return `${MESES[d.getMonth()]} ${day}`;
}

/** Retorna el día con 2 dígitos, sin mes (ej: "07"). */
export function formatDiaSolo(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return String(d.getDate()).padStart(2, "0");
}

/** Retorna "DíaSemana Día" a partir de una fecha ISO (ej: "Lunes 19"). */
export function formatDiaSemanaDia(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return `${DIAS_SEMANA[d.getDay()]} ${d.getDate()}`;
}

/** Retorna la abreviatura del día de la semana (3 letras) a partir de una
 *  fecha ISO (ej: "Mar"). */
export function formatDiaSemanaAbr(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return DIAS_SEMANA_ABR[d.getDay()];
}

/** Retorna "DD Mes" (mes abreviado y capitalizado) a partir de una fecha ISO,
 *  con el día siempre a 2 dígitos (ej: "07 Jul"). */
export function formatDiaMesAbr(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = MESES_ABR[d.getMonth()];
  return `${dia} ${mes.charAt(0).toUpperCase()}${mes.slice(1)}`;
}

/** Retorna "aaaa, mes. d" (ej: "2026, may. 31") a partir de una fecha ISO. */
export function formatFechaAnioMesDia(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return `${d.getFullYear()}, ${MESES_ABR[d.getMonth()]}. ${d.getDate()}`;
}

/** Días transcurridos desde una fecha ISO hasta hoy, **contando hoy como 1**
 *  (inclusivo): el mismo día = 1, ayer = 2, etc.
 *  @returns Número de días (≥ 1 para fechas de hoy o pasadas). */
export function daysFrom(isoDate: string): number {
  return (
    Math.floor((Date.now() - _toLocalDate(isoDate).getTime()) / 86400000) + 1
  );
}

/** Retorna "yyyy-mm-dd" a partir de un Date, usando sus componentes **locales**
 *  (no `toISOString()`, que convierte a UTC primero: en un huso horario detrás
 *  de UTC —ej. Colombia, UTC-5— cualquier hora local después de las 19:00 ya
 *  cae "mañana" en UTC, corriendo la fecha un día para adelante). */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Días (calendario) entre dos fechas ISO (`to − from`). Tolera ISO con/sin hora. */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round(
    (_toLocalDate(toIso).getTime() - _toLocalDate(fromIso).getTime()) / 86400000,
  );
}

/** Días (calendario) desde hoy hasta una fecha ISO. Positivo = futuro, 0 = hoy,
 *  negativo = pasado. Compara medianoche **local** a medianoche. */
export function daysUntil(isoDate: string): number {
  const target = _toLocalDate(isoDate).getTime();
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  return Math.round((target - today) / 86400000);
}

/** Suma `dias` a una fecha ISO y retorna "yyyy-mm-dd". Tolera ISO con o sin hora. */
export function addDays(isoDate: string, dias: number): string {
  const d = _toLocalDate(isoDate);
  d.setDate(d.getDate() + dias);
  return toISODate(d);
}

/** Retorna "d de mes de aaaa" (ej: "2 de ago de 2027") a partir de una fecha ISO. */
export function formatFechaCorta(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  return `${d.getDate()} de ${MESES_ABR[d.getMonth()]} de ${d.getFullYear()}`;
}

/** Retorna "aaaa, d mmm HH:mm" a partir de una fecha-hora ISO completa (con
 *  hora), en hora local (ej: "2026, 1 jul 16:00"). */
export function formatFechaHoraAnioMesDia(fechaHoraIso: string): string {
  const d = new Date(fechaHoraIso);
  const hora = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${d.getFullYear()}, ${d.getDate()} ${MESES_ABR[d.getMonth()]} ${hora}:${min}`;
}

/** Retorna "Mes DD, HH:mm" a partir de una fecha-hora ISO completa, en hora
 *  local y con el día siempre a 2 dígitos (ej: "Julio 07, 14:00"). A diferencia
 *  de `formatMesDia` (fechas sin hora, día sin padear), esta parsea la
 *  fecha-hora completa con `new Date` (no trunca a "yyyy-mm-dd" antes), para
 *  no correr la fecha un día por el huso horario. */
export function formatMesDiaHora(fechaHoraIso: string): string {
  const d = new Date(fechaHoraIso);
  const day = String(d.getDate()).padStart(2, "0");
  const hora = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${MESES[d.getMonth()]} ${day}, ${hora}:${min}`;
}

/** Retorna "DD Mes, DíaSemana HH:mm" a partir de una fecha-hora ISO completa,
 *  en hora local (ej: "07 Jul, Mar 00:00"). Para ejes de gráficos multi-día
 *  (Forecast), donde hace falta identificar a qué día pertenece cada hora. */
export function formatDiaMesDiaSemanaHora(fechaHoraIso: string): string {
  const d = new Date(fechaHoraIso);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = MESES_ABR[d.getMonth()];
  const mesCap = `${mes.charAt(0).toUpperCase()}${mes.slice(1)}`;
  const hora = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dia} ${mesCap}, ${DIAS_SEMANA_ABR[d.getDay()]} ${hora}:${min}`;
}

/** Retorna "HH:mm" a partir de una fecha-hora ISO completa, en hora local. */
export function formatHora(fechaHoraIso: string): string {
  const d = new Date(fechaHoraIso);
  const hora = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${hora}:${min}`;
}

/** Retorna "Mesaa" abreviado y capitalizado (ej: "Dic23") a partir de una fecha ISO. */
export function formatMesAnioCorto(isoDate: string): string {
  const d = _toLocalDate(isoDate);
  const mes = MESES_ABR[d.getMonth()];
  const anio = String(d.getFullYear()).slice(-2);
  return `${mes.charAt(0).toUpperCase()}${mes.slice(1)}${anio}`;
}

/** Fecha ISO ("yyyy-mm-dd") de hace `meses` meses, a partir de hoy. */
export function mesesAtras(meses: number): string {
  const hoy = new Date();
  return toISODate(new Date(hoy.getFullYear(), hoy.getMonth() - meses, hoy.getDate()));
}

/** Semana ISO 8601 (1–53) de una fecha ISO: la semana que contiene su jueves. */
export function getIsoWeek(isoDate: string): number {
  const target = _toLocalDate(isoDate);
  const dayNr = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNr = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNr + 3);
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / 604800000);
}
